/**
 * Iter 87 — Catálogo de dispositivos jerárquico (Equipo → Modelos).
 *
 * Sustituye a los antiguos "Catálogo equipos" (Iter 71 — lista plana de
 * strings) y "Catálogo de modelos" (Iter 83 — lista plana de dicts).
 *
 * UI en dos columnas:
 *   - Izquierda: familias/equipos (add, rename, toggle activo, drag reorder).
 *   - Derecha: modelos de la familia seleccionada (mismas operaciones).
 *
 * Reglas:
 *   - Nombres únicos case-insensitive en cada nivel (familias globales,
 *     modelos dentro de la familia).
 *   - No permite eliminar — sólo desactivar.
 */
import { useEffect, useMemo, useState } from "react";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { GripVertical, Pencil, Plus, Save, Search, X, Check, ChevronRight } from "lucide-react";
import { toast } from "sonner";

function uuid() {
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === "x" ? r : (r & 0x3) | 0x8).toString(16);
  });
}

export default function DeviceCatalogTab({ data, save, disabled }) {
  const [families, setFamilies] = useState(data?.device_catalog || []);
  const [selectedFamId, setSelectedFamId] = useState(null);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    const list = data?.device_catalog || [];
    setFamilies(list);
    setDirty(false);
    if (list.length && !list.find((f) => f.id === selectedFamId)) {
      setSelectedFamId(list[0].id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.device_catalog]);

  const selectedFam = families.find((f) => f.id === selectedFamId) || null;

  const doSave = async () => {
    await save({ device_catalog: families });
    setDirty(false);
  };

  const updateFamilies = (next) => { setFamilies(next); setDirty(true); };

  return (
    <div className="ds-card">
      <div className="px-4 py-3 border-b border-[#E5E5E0] flex items-center justify-between">
        <div>
          <div className="font-display font-bold text-sm">Catálogo de dispositivos</div>
          <div className="text-xs text-[#525252] mt-0.5">
            Estructura jerárquica <strong>Equipo → Modelo</strong>. Los modelos disponibles en la ficha de reparación dependen siempre del equipo seleccionado.
          </div>
        </div>
        <button
          type="button"
          onClick={doSave}
          disabled={disabled || !dirty}
          data-testid="devices-save"
          className="inline-flex items-center gap-2 bg-[#6E8B3D] hover:bg-[#556C2E] text-white px-3 py-2 text-sm font-semibold disabled:opacity-40"
        >
          {dirty && <span className="inline-block w-2 h-2 rounded-full bg-red-400 animate-pulse" />}
          <Save size={14} /> Guardar cambios
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 divide-x divide-[#E5E5E0]">
        <FamilyPanel
          families={families}
          selectedId={selectedFamId}
          onSelect={setSelectedFamId}
          onChange={updateFamilies}
          disabled={disabled}
        />
        <ModelPanel
          family={selectedFam}
          onFamilyChange={(nextFam) => {
            if (!nextFam) return;
            updateFamilies(families.map((f) => (f.id === nextFam.id ? nextFam : f)));
          }}
          disabled={disabled}
        />
      </div>

      <p className="px-4 py-3 text-xs text-[#525252] border-t border-[#E5E5E0] leading-relaxed">
        Los equipos y modelos no se pueden eliminar — sólo desactivar. Las reparaciones antiguas seguirán mostrando el equipo/modelo aunque estén desactivados; simplemente dejarán de aparecer en nuevas reparaciones.
      </p>
    </div>
  );
}

/* ================================================================
 *  Panel izquierdo: familias/equipos
 * =============================================================== */
function FamilyPanel({ families, selectedId, onSelect, onChange, disabled }) {
  const [q, setQ] = useState("");
  const [newName, setNewName] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editingName, setEditingName] = useState("");

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return families;
    return families.filter((f) => f.name.toLowerCase().includes(term));
  }, [families, q]);

  const nameExists = (name, exceptId = null) =>
    families.some((f) => f.id !== exceptId && f.name.trim().toLowerCase() === name.trim().toLowerCase());

  const add = () => {
    const n = newName.trim();
    if (!n) return;
    if (nameExists(n)) { toast.error("Ya existe un equipo con ese nombre"); return; }
    const nu = { id: uuid(), name: n, active: true, models: [] };
    onChange([...families, nu]);
    onSelect(nu.id);
    setNewName("");
  };

  const startEdit = (f) => { setEditingId(f.id); setEditingName(f.name); };

  const commitEdit = () => {
    const n = editingName.trim();
    if (!n) { toast.error("El nombre no puede quedar vacío"); return; }
    if (nameExists(n, editingId)) { toast.error("Ya existe un equipo con ese nombre"); return; }
    onChange(families.map((f) => (f.id === editingId ? { ...f, name: n } : f)));
    setEditingId(null); setEditingName("");
  };

  const toggleActive = (id) => {
    onChange(families.map((f) => (f.id === id ? { ...f, active: !f.active } : f)));
  };

  const onDragEnd = (result) => {
    if (!result.destination) return;
    const from = result.source.index;
    const to = result.destination.index;
    if (from === to) return;
    const movedId = filtered[from].id;
    const targetId = filtered[to].id;
    const fromFull = families.findIndex((f) => f.id === movedId);
    const toFull = families.findIndex((f) => f.id === targetId);
    const next = [...families];
    const [moved] = next.splice(fromFull, 1);
    next.splice(toFull, 0, moved);
    onChange(next);
  };

  return (
    <div className="p-4">
      <div className="font-mono text-[10px] uppercase tracking-widest text-[#525252] mb-2">
        Equipos ({families.length})
      </div>

      <div className="flex items-center gap-2 flex-wrap mb-3">
        <input
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), add())}
          placeholder="Nombre del nuevo equipo (ej: Samsung)"
          data-testid="devices-family-new-input"
          disabled={disabled}
          className="flex-1 min-w-[180px] ds-card focus:border-[#6E8B3D] outline-none px-3 py-2 text-sm rounded-none"
        />
        <button
          type="button"
          onClick={add}
          disabled={disabled || !newName.trim()}
          data-testid="devices-family-new-add"
          className="inline-flex items-center gap-2 bg-[#111110] hover:bg-[#6E8B3D] text-white px-3 py-2 text-sm disabled:opacity-40"
        >
          <Plus size={14} /> Añadir
        </button>
      </div>

      <div className="flex items-center gap-2 border-b border-[#E5E5E0] pb-2 mb-2">
        <Search size={14} className="text-[#525252]" />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Buscar equipo…"
          data-testid="devices-family-search"
          className="flex-1 bg-transparent outline-none text-sm py-1"
        />
      </div>

      {filtered.length === 0 ? (
        <div className="p-6 text-center text-sm text-[#525252]">Sin equipos.</div>
      ) : (
        <DragDropContext onDragEnd={onDragEnd}>
          <Droppable droppableId="families">
            {(provided) => (
              <div
                {...provided.droppableProps}
                ref={provided.innerRef}
                className="border border-[#E5E5E0]"
                data-testid="devices-family-list"
              >
                {filtered.map((f, index) => {
                  const isSel = f.id === selectedId;
                  return (
                    <Draggable key={f.id} draggableId={f.id} index={index}>
                      {(prov, snapshot) => (
                        <div
                          ref={prov.innerRef}
                          {...prov.draggableProps}
                          onClick={() => onSelect(f.id)}
                          className={`flex items-center gap-2 px-3 py-2 border-b border-[#E5E5E0] last:border-b-0 cursor-pointer ${
                            snapshot.isDragging ? "bg-[#F0F4E8]" :
                            isSel ? "bg-[#111110] text-white" :
                            f.active ? "bg-white hover:bg-[#FAFAF7]" : "bg-[#F7F7F4]"
                          }`}
                          data-testid={`devices-family-row-${f.id}`}
                        >
                          <span
                            {...prov.dragHandleProps}
                            className={`${isSel ? "text-[#A8A8A0]" : "text-[#A8A8A0] hover:text-[#111110]"} cursor-grab active:cursor-grabbing`}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <GripVertical size={14} />
                          </span>
                          {editingId === f.id ? (
                            <>
                              <input
                                value={editingName}
                                onChange={(e) => setEditingName(e.target.value)}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter") { e.preventDefault(); commitEdit(); }
                                  if (e.key === "Escape") { setEditingId(null); setEditingName(""); }
                                }}
                                autoFocus
                                onClick={(e) => e.stopPropagation()}
                                className="flex-1 bg-white text-[#111110] border border-[#6E8B3D] outline-none px-2 py-1 text-sm rounded-none"
                                data-testid={`devices-family-edit-input-${f.id}`}
                              />
                              <button
                                type="button"
                                onClick={(e) => { e.stopPropagation(); commitEdit(); }}
                                className={isSel ? "text-white hover:text-[#F0F4E8]" : "text-[#6E8B3D] hover:text-[#556C2E]"}
                              >
                                <Check size={14} />
                              </button>
                              <button
                                type="button"
                                onClick={(e) => { e.stopPropagation(); setEditingId(null); setEditingName(""); }}
                                className={isSel ? "text-white hover:text-red-300" : "text-[#525252] hover:text-red-600"}
                              >
                                <X size={14} />
                              </button>
                            </>
                          ) : (
                            <>
                              <span
                                className={`flex-1 text-sm ${!f.active ? "line-through opacity-60" : ""}`}
                                data-testid={`devices-family-name-${f.id}`}
                              >
                                {f.name}
                              </span>
                              <span className={`text-[10px] font-mono ${isSel ? "text-[#A8A8A0]" : "text-[#525252]"}`}>
                                {(f.models || []).length}
                              </span>
                              <button
                                type="button"
                                onClick={(e) => { e.stopPropagation(); startEdit(f); }}
                                disabled={disabled}
                                className={isSel ? "text-white hover:text-[#F0F4E8]" : "text-[#525252] hover:text-[#6E8B3D]"}
                                data-testid={`devices-family-edit-${f.id}`}
                              >
                                <Pencil size={12} />
                              </button>
                              <button
                                type="button"
                                onClick={(e) => { e.stopPropagation(); toggleActive(f.id); }}
                                disabled={disabled}
                                data-testid={`devices-family-toggle-${f.id}`}
                                className={`text-[9px] font-mono uppercase tracking-wider px-1.5 py-0.5 border ${
                                  isSel
                                    ? "border-[#A8A8A0] text-[#A8A8A0] hover:bg-white hover:text-[#111110]"
                                    : f.active
                                      ? "border-[#6E8B3D] text-[#6E8B3D] hover:bg-[#6E8B3D] hover:text-white"
                                      : "border-[#A8A8A0] text-[#A8A8A0] hover:bg-[#111110] hover:text-white hover:border-[#111110]"
                                }`}
                              >
                                {f.active ? "ON" : "OFF"}
                              </button>
                              <ChevronRight size={14} className={isSel ? "text-white" : "text-[#A8A8A0]"} />
                            </>
                          )}
                        </div>
                      )}
                    </Draggable>
                  );
                })}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      )}
    </div>
  );
}

/* ================================================================
 *  Panel derecho: modelos de la familia seleccionada
 * =============================================================== */
function ModelPanel({ family, onFamilyChange, disabled }) {
  const [q, setQ] = useState("");
  const [newName, setNewName] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editingName, setEditingName] = useState("");

  useEffect(() => {
    setQ(""); setNewName(""); setEditingId(null); setEditingName("");
  }, [family?.id]);

  if (!family) {
    return (
      <div className="p-6 text-center text-sm text-[#525252]">
        Selecciona un equipo para ver sus modelos.
      </div>
    );
  }

  const models = family.models || [];
  const nameExists = (name, exceptId = null) =>
    models.some((m) => m.id !== exceptId && m.name.trim().toLowerCase() === name.trim().toLowerCase());

  const filtered = models.filter((m) =>
    !q.trim() || m.name.toLowerCase().includes(q.trim().toLowerCase()),
  );

  const commit = (next) => onFamilyChange({ ...family, models: next });

  const add = () => {
    const n = newName.trim();
    if (!n) return;
    if (nameExists(n)) { toast.error(`Ya existe un modelo con ese nombre en ${family.name}`); return; }
    commit([...models, { id: uuid(), name: n, active: true }]);
    setNewName("");
  };

  const startEdit = (m) => { setEditingId(m.id); setEditingName(m.name); };
  const commitEdit = () => {
    const n = editingName.trim();
    if (!n) { toast.error("El nombre no puede quedar vacío"); return; }
    if (nameExists(n, editingId)) { toast.error("Ya existe un modelo con ese nombre"); return; }
    commit(models.map((m) => (m.id === editingId ? { ...m, name: n } : m)));
    setEditingId(null); setEditingName("");
  };
  const toggleActive = (id) => commit(models.map((m) => (m.id === id ? { ...m, active: !m.active } : m)));
  const onDragEnd = (result) => {
    if (!result.destination) return;
    const from = result.source.index;
    const to = result.destination.index;
    if (from === to) return;
    const movedId = filtered[from].id;
    const targetId = filtered[to].id;
    const fromFull = models.findIndex((m) => m.id === movedId);
    const toFull = models.findIndex((m) => m.id === targetId);
    const next = [...models];
    const [moved] = next.splice(fromFull, 1);
    next.splice(toFull, 0, moved);
    commit(next);
  };

  return (
    <div className="p-4">
      <div className="font-mono text-[10px] uppercase tracking-widest text-[#525252] mb-2">
        Modelos de <strong className="text-[#111110]">{family.name}</strong> ({models.length})
      </div>

      <div className="flex items-center gap-2 flex-wrap mb-3">
        <input
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), add())}
          placeholder={`Añadir modelo a ${family.name}…`}
          data-testid="devices-model-new-input"
          disabled={disabled}
          className="flex-1 min-w-[180px] ds-card focus:border-[#6E8B3D] outline-none px-3 py-2 text-sm rounded-none"
        />
        <button
          type="button"
          onClick={add}
          disabled={disabled || !newName.trim()}
          data-testid="devices-model-new-add"
          className="inline-flex items-center gap-2 bg-[#111110] hover:bg-[#6E8B3D] text-white px-3 py-2 text-sm disabled:opacity-40"
        >
          <Plus size={14} /> Añadir
        </button>
      </div>

      <div className="flex items-center gap-2 border-b border-[#E5E5E0] pb-2 mb-2">
        <Search size={14} className="text-[#525252]" />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Buscar modelo…"
          data-testid="devices-model-search"
          className="flex-1 bg-transparent outline-none text-sm py-1"
        />
      </div>

      {filtered.length === 0 ? (
        <div className="p-6 text-center text-sm text-[#525252]">
          {models.length === 0 ? "Sin modelos. Añade el primero arriba." : "Ningún modelo coincide."}
        </div>
      ) : (
        <DragDropContext onDragEnd={onDragEnd}>
          <Droppable droppableId={`models-${family.id}`}>
            {(provided) => (
              <div
                {...provided.droppableProps}
                ref={provided.innerRef}
                className="border border-[#E5E5E0]"
                data-testid="devices-model-list"
              >
                {filtered.map((m, index) => (
                  <Draggable key={m.id} draggableId={m.id} index={index}>
                    {(prov, snapshot) => (
                      <div
                        ref={prov.innerRef}
                        {...prov.draggableProps}
                        className={`flex items-center gap-2 px-3 py-2 border-b border-[#E5E5E0] last:border-b-0 ${
                          snapshot.isDragging ? "bg-[#F0F4E8]" : m.active ? "bg-white" : "bg-[#F7F7F4]"
                        }`}
                        data-testid={`devices-model-row-${m.id}`}
                      >
                        <span
                          {...prov.dragHandleProps}
                          className="text-[#A8A8A0] hover:text-[#111110] cursor-grab active:cursor-grabbing"
                        >
                          <GripVertical size={14} />
                        </span>
                        {editingId === m.id ? (
                          <>
                            <input
                              value={editingName}
                              onChange={(e) => setEditingName(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === "Enter") { e.preventDefault(); commitEdit(); }
                                if (e.key === "Escape") { setEditingId(null); setEditingName(""); }
                              }}
                              autoFocus
                              className="flex-1 bg-white border border-[#6E8B3D] outline-none px-2 py-1 text-sm rounded-none"
                              data-testid={`devices-model-edit-input-${m.id}`}
                            />
                            <button type="button" onClick={commitEdit} className="text-[#6E8B3D] hover:text-[#556C2E]"><Check size={14} /></button>
                            <button type="button" onClick={() => { setEditingId(null); setEditingName(""); }} className="text-[#525252] hover:text-red-600"><X size={14} /></button>
                          </>
                        ) : (
                          <>
                            <span
                              className={`flex-1 text-sm ${m.active ? "text-[#111110]" : "text-[#A8A8A0] line-through"}`}
                              data-testid={`devices-model-name-${m.id}`}
                            >
                              {m.name}
                            </span>
                            <button
                              type="button"
                              onClick={() => startEdit(m)}
                              disabled={disabled}
                              className="text-[#525252] hover:text-[#6E8B3D]"
                              data-testid={`devices-model-edit-${m.id}`}
                            >
                              <Pencil size={12} />
                            </button>
                            <button
                              type="button"
                              onClick={() => toggleActive(m.id)}
                              disabled={disabled}
                              data-testid={`devices-model-toggle-${m.id}`}
                              className={`text-[9px] font-mono uppercase tracking-wider px-1.5 py-0.5 border ${
                                m.active
                                  ? "border-[#6E8B3D] text-[#6E8B3D] hover:bg-[#6E8B3D] hover:text-white"
                                  : "border-[#A8A8A0] text-[#A8A8A0] hover:bg-[#111110] hover:text-white hover:border-[#111110]"
                              }`}
                            >
                              {m.active ? "ON" : "OFF"}
                            </button>
                          </>
                        )}
                      </div>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      )}
    </div>
  );
}
